import React, { useState, useMemo } from 'react';
import { X, MapPin, Clock, Calendar, Phone, Hash, ChevronLeft, ChevronRight, ExternalLink, FileText, Mail, DollarSign, Tag } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import { useSettings } from '../context/SettingsContext';

const BoatModal = ({ boat, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = boat.imageUrls || [];
  
  const { showWholesalePrice, showMarginPrice, formatPriceWithMargin } = useSettings();
  
  // Process image URLs synchronously using useMemo
  const processedImages = useMemo(() => {
    if (images.length === 0) return [];
    return images.map(url => getImageUrl(url, true));
  }, [images]);

  const formatPrice = (price) => {
    if (!price) return 'Contact for Price';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageError = (e) => {
    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect width="800" height="600" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="24"%3EImage Not Available%3C/text%3E%3C/svg%3E';
  };

  // Clean HTML from description
  const cleanDescription = (html) => {
    if (!html) return 'No description available';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || 'No description available';
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="modal-body">
          {/* Always show gallery section for consistent layout */}
          <div className="modal-gallery">
            {images.length > 0 ? (
              <>
                <div className="main-image-container">
                  <img
                    src={processedImages[currentImageIndex]}
                    alt={`${boat.title} - Image ${currentImageIndex + 1}`}
                    onError={handleImageError}
                  />
                  {images.length > 1 && (
                    <>
                      <button className="gallery-nav prev" onClick={prevImage}>
                        <ChevronLeft size={24} />
                      </button>
                      <button className="gallery-nav next" onClick={nextImage}>
                        <ChevronRight size={24} />
                      </button>
                      <div className="gallery-indicators">
                        {images.map((_, index) => (
                          <button
                            key={index}
                            className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(index)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  <div className="image-counter">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </div>
                
                {/* Thumbnail strip */}
                {images.length > 1 && (
                  <div className="thumbnail-strip">
                    {images.slice(0, 6).map((img, index) => (
                      <button
                        key={index}
                        className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                      >
                        <img 
                          src={processedImages[index]} 
                          alt={`Thumbnail ${index + 1}`} 
                          onError={handleImageError}
                        />
                      </button>
                    ))}
                    {images.length > 6 && (
                      <div className="thumbnail more-images">
                        +{images.length - 6} more
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="no-images-placeholder">
                <svg width="100%" height="100%" viewBox="0 0 400 300" xmlns="http://www.w3.org/2000/svg">
                  <rect width="400" height="300" fill="#f5f5f5"/>
                  <g transform="translate(200, 150)">
                    <rect x="-40" y="-30" width="80" height="60" fill="#e0e0e0" rx="4"/>
                    <circle cx="0" cy="0" r="15" fill="#ccc"/>
                    <path d="M-25,-15 L-15,-5 L-5,-10 L5,0 L15,-8 L25,0 L25,-15 Z" fill="#e0e0e0"/>
                  </g>
                  <text x="200" y="220" textAnchor="middle" fill="#999" fontSize="14" fontFamily="sans-serif">
                    No Images Available
                  </text>
                </svg>
              </div>
            )}
          </div>

          <div className="modal-info">
            <div className="modal-header">
              <h2>{boat.title || `${boat.year} ${boat.model}`}</h2>
              <div className="price-section">
                <div className="modal-price">{formatPrice(boat.price)}</div>
                
                {showWholesalePrice && boat.wholesalePrice && (
                  <div className="wholesale-price">
                    <Tag size={14} />
                    <span>Wholesale: {formatPrice(boat.wholesalePrice)}</span>
                  </div>
                )}
                
                {showMarginPrice && boat.wholesalePrice && (
                  <div className="sale-price">
                    <DollarSign size={14} />
                    <span>Sale Price: {formatPriceWithMargin(boat.wholesalePrice)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-specs">
              <h3>Specifications</h3>
              <div className="specs-grid">
                <div className="spec-item">
                  <span className="spec-label">Year</span>
                  <span className="spec-value">{boat.year || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Model</span>
                  <span className="spec-value">{boat.model || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Hours</span>
                  <span className="spec-value">{boat.hours ? `${boat.hours} hrs` : 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Length</span>
                  <span className="spec-value">{boat.lengthFeet ? `${boat.lengthFeet} ft` : 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Stock #</span>
                  <span className="spec-value">{boat.stocknumber || 'N/A'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">Listed</span>
                  <span className="spec-value">{formatDate(boat.showDate)}</span>
                </div>
              </div>
            </div>

            <div className="modal-location">
              <h3>Location</h3>
              <div className="location-info">
                <MapPin size={18} />
                <span>
                  {boat.locationCity && boat.locationCity !== 'Available' ? 
                    `${boat.locationCity}, ${boat.locationState}` : 
                    `ZIP: ${boat.zipCode || 'Contact for location'}`
                  }
                </span>
              </div>
              {boat.phone && (
                <div className="location-info">
                  <Phone size={18} />
                  <span>{boat.phone}</span>
                </div>
              )}
            </div>

            <div className="modal-description">
              <h3>Description</h3>
              <p>{cleanDescription(boat.description)}</p>
            </div>

            {boat.videoUrl && (
              <div className="modal-video">
                <a href={boat.videoUrl} target="_blank" rel="noopener noreferrer" className="video-link">
                  <ExternalLink size={16} />
                  View Video
                </a>
              </div>
            )}

            {/* Build Sheet Section */}
            {boat.buildSheetURL && (
              <div className="build-sheet-section">
                <h3>Build Sheet</h3>
                <button 
                  className="build-sheet-button"
                  onClick={() => window.open(boat.buildSheetURL, '_blank')}
                >
                  <FileText size={20} />
                  <span>View Build Sheet PDF</span>
                  <ExternalLink size={16} />
                </button>
                <p className="build-sheet-note">PDF Document - Opens in new window</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="modal-actions">
              <button className="action-button primary" onClick={() => window.location.href = `tel:8014913710`}>
                <Phone size={18} />
                Contact Dealer
              </button>
              <button className="action-button secondary" onClick={() => window.location.href = `mailto:sales@boardco.com?subject=Inquiry about ${boat.title || boat.model}`}>
                <Mail size={18} />
                Email Inquiry
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoatModal;
